import { useEffect, useRef, useState } from 'react';

export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const satelliteRef = useRef(null);
  const labelRef = useRef(null);
  const rippleContainerRef = useRef(null);

  const mouse = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const rafId = useRef(null);
  const angleRef = useRef(0);

  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const isHovered = useRef(false);
  const hoverType = useRef('default');
  const isClicking = useRef(false);
  const isVisible = useRef(false);

  useEffect(() => {
    // Check for touch / mobile screen
    const isTouch = window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    if (isTouch || window.innerWidth < 768) {
      setIsTouchDevice(true);
      return;
    }

    const dot = dotRef.current;
    const ring = ringRef.current;
    const satellite = satelliteRef.current;
    const label = labelRef.current;
    if (!dot || !ring) return;

    const onMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      if (!isVisible.current) {
        isVisible.current = true;
        dot.style.opacity = '1';
        ring.style.opacity = '1';
        if (satellite) satellite.style.opacity = '1';
      }
    };

    const onMouseEnterWindow = () => {
      isVisible.current = true;
      dot.style.opacity = '1';
      ring.style.opacity = '1';
      if (satellite) satellite.style.opacity = '1';
    };

    const onMouseLeaveWindow = () => {
      isVisible.current = false;
      dot.style.opacity = '0';
      ring.style.opacity = '0';
      if (satellite) satellite.style.opacity = '0';
      if (label) label.style.opacity = '0';
    };

    const onMouseDown = (e) => {
      isClicking.current = true;

      // Spawn a dynamic acoustic / bio-pulse ripple
      if (rippleContainerRef.current) {
        const ripple = document.createElement('div');
        ripple.className = 'cursor-click-ripple';
        ripple.style.left = `${e.clientX}px`;
        ripple.style.top = `${e.clientY}px`;
        rippleContainerRef.current.appendChild(ripple);
        setTimeout(() => {
          if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
        }, 700);
      }
    };

    const onMouseUp = () => {
      isClicking.current = false;
    };

    const onEnterHover = (e) => {
      isHovered.current = true;
      const target = e.currentTarget;
      const isCard = target.closest('#projects') || target.tagName === 'A';
      hoverType.current = isCard ? 'card' : 'button';

      if (label) {
        label.textContent = isCard ? 'VIEW ↗' : 'CLICK';
        label.style.opacity = '1';
        label.style.transform = 'scale(1)';
      }

      ring.style.width = '72px';
      ring.style.height = '72px';
      ring.style.backgroundColor = 'rgba(232, 149, 109, 0.12)';
      ring.style.borderColor = 'var(--accent)';
      ring.style.boxShadow = '0 0 20px rgba(232, 149, 109, 0.35)';

      dot.style.transform = `translate(${mouse.current.x - 4}px, ${mouse.current.y - 4}px) scale(0)`;
      if (satellite) satellite.style.opacity = '0';
    };

    const onLeaveHover = () => {
      isHovered.current = false;
      hoverType.current = 'default';

      if (label) {
        label.style.opacity = '0';
        label.style.transform = 'scale(0.6)';
      }

      ring.style.width = '36px';
      ring.style.height = '36px';
      ring.style.backgroundColor = 'transparent';
      ring.style.borderColor = 'rgba(45, 23, 83, 0.3)';
      ring.style.boxShadow = 'none';

      dot.style.transform = `translate(${mouse.current.x - 4}px, ${mouse.current.y - 4}px) scale(1)`;
      if (satellite) satellite.style.opacity = '1';
    };

    const attachHoverListeners = () => {
      document.querySelectorAll('a, button, [data-cursor-hover], input, textarea').forEach((el) => {
        el.removeEventListener('mouseenter', onEnterHover);
        el.removeEventListener('mouseleave', onLeaveHover);
        el.addEventListener('mouseenter', onEnterHover);
        el.addEventListener('mouseleave', onLeaveHover);
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseenter', onMouseEnterWindow);
    window.addEventListener('mouseleave', onMouseLeaveWindow);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);

    attachHoverListeners();

    const observer = new MutationObserver(() => attachHoverListeners());
    observer.observe(document.body, { childList: true, subtree: true });

    // Smooth physics loop
    const lerp = (start, end, factor) => start + (end - start) * factor;

    const render = () => {
      // Immediate precision central core dot
      if (!isHovered.current) {
        dot.style.transform = `translate(${mouse.current.x - 4}px, ${mouse.current.y - 4}px) scale(1)`;
      }

      // Fluid trailing ring with spring easing
      ringPos.current.x = lerp(ringPos.current.x, mouse.current.x, 0.16);
      ringPos.current.y = lerp(ringPos.current.y, mouse.current.y, 0.16);

      const radius = isHovered.current ? 36 : 18;
      const scale = isClicking.current ? 0.8 : 1;
      ring.style.transform = `translate(${ringPos.current.x - radius}px, ${ringPos.current.y - radius}px) scale(${scale})`;

      // Center the contextual label inside the ring
      if (label && isHovered.current) {
        label.style.transform = `translate(${ringPos.current.x}px, ${ringPos.current.y}px) translate(-50%, -50%) scale(1)`;
      }

      // Planetary satellite orbit calculation around the ring
      if (satellite && !isHovered.current) {
        angleRef.current += 0.05;
        const orbitRadius = 24;
        const satX = ringPos.current.x + Math.cos(angleRef.current) * orbitRadius - 3;
        const satY = ringPos.current.y + Math.sin(angleRef.current) * orbitRadius - 3;
        satellite.style.transform = `translate(${satX}px, ${satY}px)`;
      }

      rafId.current = requestAnimationFrame(render);
    };

    rafId.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseenter', onMouseEnterWindow);
      window.removeEventListener('mouseleave', onMouseLeaveWindow);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      observer.disconnect();
    };
  }, []);

  if (isTouchDevice) return null;

  return (
    <>
      <style>{`
        .cursor-click-ripple {
          position: fixed;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid var(--accent);
          pointer-events: none;
          z-index: 99995;
          transform: translate(-50%, -50%);
          animation: cursorRipple 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        @keyframes cursorRipple {
          0% {
            width: 8px;
            height: 8px;
            opacity: 0.9;
          }
          100% {
            width: 85px;
            height: 85px;
            opacity: 0;
            border-width: 1px;
          }
        }
      `}</style>

      {/* Ripple Container */}
      <div ref={rippleContainerRef} />

      {/* Radiant Central Core Dot */}
      <div
        ref={dotRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: 'var(--accent)',
          boxShadow: '0 0 10px var(--accent)',
          pointerEvents: 'none',
          zIndex: 100000,
          opacity: 0,
          willChange: 'transform',
          transition: 'opacity 0.25s ease, transform 0.2s ease',
        }}
      />

      {/* Orbiting Satellite Node */}
      <div
        ref={satelliteRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: 'var(--text)',
          pointerEvents: 'none',
          zIndex: 99998,
          opacity: 0,
          willChange: 'transform',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Ambient Fluid Aperture Ring */}
      <div
        ref={ringRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '1.5px solid rgba(45, 23, 83, 0.3)',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          zIndex: 99999,
          opacity: 0,
          willChange: 'transform, width, height',
          transition:
            'opacity 0.25s ease, width 0.3s cubic-bezier(0.16, 1, 0.3, 1), height 0.3s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
        }}
      />

      {/* Contextual Action Micro-Label */}
      <div
        ref={labelRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: 'var(--text)',
          pointerEvents: 'none',
          zIndex: 100001,
          opacity: 0,
          transform: 'scale(0.6)',
          whiteSpace: 'nowrap',
          willChange: 'transform, opacity',
          transition: 'opacity 0.2s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
    </>
  );
}
